import { Fragment } from "react";
import { DEMO_URL } from "../../config";
import {
  inr, num, fmtDate, has, labelled, joinParts,
  Html, initials, Signature, deriveBasics, missingClientFields, applyCounts,
} from "./documentBasics";

/**
 * The proposal — three print-ready pages.
 *
 * Nothing here is authored in the frontend. Prices come from
 * /proposals/calculate; every word comes from /proposal-template/rendered,
 * which hands the sections back already ordered, already filtered to the
 * enabled ones and already resolved for this segment.
 *
 * The quotation is a separate document (QuotationDocument), not a fourth page.
 */

const ProposalDocument = ({ form, quote, template, quoteNo, segmentLabel }) => {
  const missing = missingClientFields(form);
  if (missing.length) {
    return (
      <div className="empty">
        <b>Add the prospect’s details to generate a personalised proposal.</b>
        <br /><br />
        Required: {missing.join(", ")}.
      </div>
    );
  }
  if (!quote) return <div className="empty">Fill the pricing inputs to build the proposal.</div>;

  const {
    company, client, seg, audience, today, validity,
    isCustom, p, li, d, months, termLabel, termWord, firstLabel, clientPhone,
    attorneyNumbers,
    periodDays, grace, termLength, totalDiscPct, discountLabel, showRateRow,
    onboardingSaving, fill, counts,
  } = deriveBasics({ form, quote, template, segmentLabel });

  const features = quote.plan_details?.features || [];

  /* The rendered endpoint hands back exactly what to print: sections already in
     order, already filtered to the enabled ones, already resolved for this
     segment. Nothing here decides what goes where any more. */
  const sections = template?.sections || [];
  const coverSubtitle = template?.cover_subtitle;
  /**
   * One renderer per section type. Returns null when a section carries nothing
   * worth printing, so a reordered document never shows an empty heading.
   *
   * `title` is what the heading shows — it is editable per section, and it is
   * not the same thing as the type.
   */
  const renderSection = (section) => {
    const c = section.content || {};
    const heading = section.title || c.heading;

    switch (section.type) {
      case "executive_summary": {
        if (!c.body) return null;
        return (
          <div className="sec">
            {heading && <h3>{fill(heading)}</h3>}
            {c.subheading && <Html as="p" className="sh" html={fill(c.subheading)} />}
            <Html
              as="p"
              style={{ fontSize: 12.5, color: "#2A3547", lineHeight: 1.65, margin: 0 }}
              html={fill(c.body)}
            />
          </div>
        );
      }

      case "why_entermark": {
        const stats = c.stats || [];
        const cards = c.cards || [];
        if (!stats.length && !cards.length) return null;
        return (
          <div className="sec">
            {heading && <h3>{fill(heading)}</h3>}
            {c.subheading && <Html as="p" className="sh" html={fill(c.subheading)} />}
            {stats.length > 0 && (
              <div className="tiles">
                {stats.map((x, i) => (
                  <div className="tile" key={i}><b>{x.value}</b><span>{x.label}</span></div>
                ))}
              </div>
            )}
            {cards.length > 0 && (
              <div className="grid2c" style={{ marginTop: 12 }}>
                {cards.map((x, i) => (
                  <div className="card" key={i}>
                    <h4>{x.title}</h4>
                    <Html as="p" html={fill(x.description)} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }

      case "offerings": {
        const items = c.items || [];
        if (!items.length) return null;
        return (
          <div className="topoffers">
            <div className="oh">
              <span className="tag">{fill(heading) || "Top offerings"}</span>
            </div>
            <div className="offers">
              {items.map((o, i) => (
                <div className="offer" key={i}>
                  <div className="ic">{o.icon}</div>
                  <div><h4>{o.title}</h4><Html as="p" html={fill(o.description)} /></div>
                </div>
              ))}
            </div>
          </div>
        );
      }

      case "list": {
        const items = c.items || [];
        if (!items.length) return null;
        return (
          <div className="sec">
            {heading && <h3>{fill(heading)}</h3>}
            {/* A shared list can state a seat or client count too. */}
            <ul className="feats">{items.map((x, i) => <li key={i}>{applyCounts(x, counts)}</li>)}</ul>
          </div>
        );
      }

      case "steps": {
        const steps = c.steps || [];
        if (!steps.length) return null;
        return (
          <div className="sec">
            {heading && <h3>{fill(heading)}</h3>}
            {/* Ordered on purpose — these are sequential, not a menu. */}
            <ol style={{ fontSize: 12.5, color: "#2A3547", lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
              {steps.map((x, i) => <Html as="li" key={i} html={fill(x)} />)}
            </ol>
          </div>
        );
      }

      case "founder": {
        if (!c.name) return null;
        return (
          <div className="sec">
            {heading && <h3>{fill(heading)}</h3>}
            {c.subheading && <Html as="p" className="sh" html={fill(c.subheading)} />}
            <div className="leader">
              {/* An absolute URL when configured; initials otherwise, so the card
                  never renders a broken image. */}
              {c.photo_url
                ? <img src={c.photo_url} alt={c.name} />
                : <div className="mono-av">{initials(c.name)}</div>}
              <div>
                <div className="lname">{c.name}</div>
                {c.title && <div className="ltitle">{c.title}</div>}
                {c.bio && <Html as="p" html={fill(c.bio)} />}
                {c.quote && <p className="lcomment">“{c.quote}”</p>}
              </div>
            </div>
          </div>
        );
      }

      case "terms": {
        const items = c.items || [];
        if (!items.length) return null;
        return (
          <div className="sec">
            {heading && <h3>{fill(heading)}</h3>}
            <ol className="terms">{items.map((x, i) => <Html as="li" key={i} html={fill(x)} />)}</ol>
          </div>
        );
      }

      case "acceptance": {
        /* Two signature blocks. The client's is blank by design — it is signed
           after printing; ours is filled from the Company Profile. */
        return (
          <div className="sec">
            {heading && <h3>{fill(heading)}</h3>}
            {c.subheading && <Html as="p" className="sh" html={fill(c.subheading)} />}
            <div className="accept">
              <div className="sigcard">
                <div className="who">{fill(c.client_label)}</div>
                <div className="org">{client}</div>
                {c.client_note && <Html as="div" className="cin" html={fill(c.client_note)} />}
                <div className="sigbox"><span>{fill(c.sign_label)}</span></div>
                <div className="sigrow">
                  <div className="fld"><label>{fill(c.name_label)}</label></div>
                  <div className="fld"><label>{fill(c.designation_label)}</label></div>
                  <div className="fld wide"><label>{fill(c.date_label)}</label></div>
                </div>
              </div>
              <div className="sigcard alt">
                <div className="who">{fill(c.company_label)}</div>
                <div className="org">{company.legal_name}</div>
                <div className="cin">{joinParts([labelled("CIN", company.cin), company.city])}</div>
                <div className="sigbox">
                  {/* The signature is the section's; the seal is drawn from
                      the company's own name and city. */}
                  <Signature
                    sealId="pseal" imageUrl={c.signature_url ?? company.signature_url}
                    legalName={company.legal_name} city={company.city}
                  />
                  <span>{fill(c.sign_label)}</span>
                </div>
                <div className="sigrow">
                  <div className="fld"><label>{fill(c.name_label)}</label>&nbsp;<b>{c.signatory_name || company.signatory_name}</b></div>
                  <div className="fld"><label>{fill(c.designation_label)}</label>&nbsp;<b>{c.signatory_role || company.signatory_role}</b></div>
                  <div className="fld wide"><label>{fill(c.date_label)}</label>&nbsp;<b>{fmtDate(today)}</b></div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case "richtext": {
        if (!c.html) return null;
        return (
          <div className="sec">
            {heading && <h3>{fill(heading)}</h3>}
            <Html as="div" style={{ fontSize: 12.5, color: "#2A3547", lineHeight: 1.65 }} html={fill(c.html)} />
          </div>
        );
      }

      default:
        // An unknown type from a newer backend is skipped, not crashed on.
        return null;
    }
  };

  /* The fixed blocks sit where the reference document puts them: the pricing
     follows the executive summary — whose closing line is "your recommended plan
     and investment are below" — and the breakdown opens the closing page, ahead
     of the terms. Everything between them is the admin's order, printed as it
     arrives. */
  const execAt = sections.findIndex((x) => x.type === "executive_summary");
  const pricingAfter = execAt >= 0 ? execAt : 0;
  const termsAt = sections.findIndex((x) => x.type === "terms");
  const breakdownBefore = termsAt >= 0 ? termsAt : sections.length;

  const draw = (from, to) =>
    sections.slice(from, to).map((section) => (
      <Fragment key={section.key}>{renderSection(section)}</Fragment>
    ));

  return (
    <div className={`theme-${seg}`}>

      {/* ── PAGE 1 ── */}
      <div className="page">
        <span className="pagetag">1 / 3</span>
        <div className="cover">
          <span className="mono">™</span>
          <div className="covtop">
            <div style={{ display: "flex", alignItems: "center", gap: 9, fontWeight: 800, fontSize: 16 }}>
              <span style={{ width: 26, height: 26, borderRadius: 8, background: "var(--grad)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {(company.brand_name || "E").charAt(0)}
              </span>
              {company.brand_name}
            </div>
            <div className="rate"><span className="stars">★★★★★</span> 4.9/5 · 1,000+ IP pros</div>
          </div>
          <div className="lux-badge">◆ World's First AI-Powered Trademark Suite</div>
          <div className="kick" style={{ marginTop: 14 }}>
            Proposal · Prepared exclusively for {form.contact_name || client} · Confidential
          </div>
          <h1>
            {seg === "attorney" ? "AI trademark practice platform for" : "AI trademark protection for"}
            <br /><span className="hl">{client}</span>
          </h1>
          {coverSubtitle && (
            <Html as="div" style={{ color: "#C9CCEB", fontSize: 13, maxWidth: 540 }} html={fill(coverSubtitle)} />
          )}
          <div className="covchips">
            <span className="covchip"><span className="cl">WEB</span> <b>{company.website}</b></span>
            <span className="covchip"><span className="cl">EMAIL</span> <b>{company.email}</b></span>
            <span className="covchip"><span className="cl">WHATSAPP</span> <b>{company.whatsapp}</b></span>
          </div>
          <div className="meta">
            Prepared for <b>{form.contact_name || "—"}</b> · {client}
            {form.email && <> · <b>{form.email}</b></>}
            {clientPhone && <> · <b>{clientPhone}</b></>}
            <br />{audience} &nbsp;·&nbsp; {fmtDate(today)} &nbsp;·&nbsp;
            <b>{num(quote.marks)} trademarks</b> &nbsp;·&nbsp; <b>{form.users || 1} users</b>
            {attorneyNumbers && <><br />Attorney numbers &nbsp;<b>{attorneyNumbers}</b></>}
          </div>
        </div>

        {draw(0, pricingAfter + 1)}

        <div className="sec">
          <h3>Your plan &amp; investment</h3>
          <p className="sh">
            Per month, billed {termLabel.toLowerCase()} · 12-month lock-in · GST {p.gst_percent ?? 18}% extra ·
            government registry fees at cost.
          </p>
          <table className="pt"><tbody>
            {isCustom ? (
              <>
                <tr><td>Package</td><td>Enterprise — custom</td></tr>
                <tr className="big"><td>Pricing</td><td>Custom quote (talk to sales)</td></tr>
              </>
            ) : (
              <>
                <tr><td>Package</td><td>{quote.plan}</td></tr>
                {showRateRow && (
                  <tr>
                    <td>Per-trademark rate</td>
                    <td>
                      ₹{num(li.per_mark_rate_yr)}/mark/yr{" "}
                      <span style={{ color: "var(--mut2)" }}>× {num(quote.marks)} marks</span>
                    </td>
                  </tr>
                )}
                <tr>
                  <td>Payment</td>
                  <td>
                    {termLabel}
                    {discountLabel ? ` · ${discountLabel} discount` : " · no discount"}
                  </td>
                </tr>
                <tr><td>Subscription term</td><td>{termLength}</td></tr>
                <tr><td>Billing cycle</td><td>Every {periodDays} days · {grace}-day grace period</td></tr>
                <tr><td>List price / month</td><td>{inr(li.base_monthly)}</td></tr>
                {li.addons_monthly > 0 && <tr><td>Add-ons / month</td><td>{inr(li.addons_monthly)}</td></tr>}
                {d.discount_amount_monthly > 0 && (
                  <tr>
                    <td>Discount ({totalDiscPct}%)</td>
                    <td style={{ color: "var(--emr)" }}>− {inr(d.discount_amount_monthly)}</td>
                  </tr>
                )}
                <tr className="big"><td>Net price / month</td><td>{inr(p.monthly_net)}</td></tr>
                {p.onboarding > 0 && (
                  <tr>
                    <td>
                      One-time onboarding
                      {d.onboarding_percent > 0 && (
                        <span style={{ color: "var(--emr)", fontWeight: 700 }}> ({d.onboarding_percent}% off)</span>
                      )}
                    </td>
                    <td>
                      {d.onboarding_percent > 0
                        ? <><s style={{ color: "var(--mut2)" }}>{inr(p.onboarding)}</s> &nbsp;{inr(p.onboarding_net)}</>
                        : inr(p.onboarding)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td>{termLabel} invoice (incl. {p.gst_percent ?? 18}% GST)</td>
                  <td>{inr(p.term_invoice)}</td>
                </tr>
              </>
            )}
          </tbody></table>
          {form.notes && (
            <p style={{ fontSize: 12, color: "var(--mut)", marginTop: 12 }}><b>Note:</b> {form.notes}</p>
          )}
        </div>
      </div>

      {/* ── PAGE 2 ── */}
      <div className="page">
        <span className="pagetag">2 / 3</span>
        <div className="sec">
          {/* The tier that was quoted, not the plan record behind it. A band can
              draw its features from a plan with a different name — the client
              was quoted "Business" and should not be handed "Enterprise Pack"
              two lines later. */}
          <h3>What {client} gets <span className="pill">{quote.plan || quote.plan_details?.name}</span></h3>
          {quote.plan_details?.tagline && <p className="sh">{quote.plan_details.tagline}</p>}
          {/* Said once and plainly, so the client does not have to read the
              numbers out of the feature lines. */}
          <p className="sh" style={{ marginTop: 0 }}>
            This plan covers{" "}
            <b style={{ color: "var(--ink)" }}>{num(counts.users)} user seats</b>
            {counts.clients != null && (
              <> &middot; <b style={{ color: "var(--ink)" }}>
                {Number(counts.clients) === -1 ? "Unlimited" : num(counts.clients)} clients
              </b></>
            )}
            {" "}&middot; <b style={{ color: "var(--ink)" }}>{num(counts.marks)} trademarks</b>.
          </p>
          {features.length > 0 ? (
            <ul className="feats">
              {features.map((f, i) => (
                <li key={i} style={f.highlight ? { fontWeight: 700, color: "var(--ink)" } : undefined}>
                  {applyCounts(f.name, counts)}
                </li>
              ))}
            </ul>
          ) : (
            /* plan_id is unmapped on the rate card — say so instead of printing
               an empty section into a client-facing document. */
            <p style={{ fontSize: 12.5, color: "var(--amb)" }}>
              No features are mapped to this plan yet — set its plan on the Rate Card.
            </p>
          )}
        </div>
        {/* The sections arrive in the order the admin set. The fixed anchors —
            cover, pricing, features, the cost breakdown, signatures and the
            quotation — are not sections and stay where they are. */}

        {draw(pricingAfter + 1, breakdownBefore)}
      </div>

      {/* ── PAGE 3 ── */}
      <div className="page">
        <span className="pagetag">3 / 3</span>
        {!isCustom && (
          <div className="sec">
            <h3>Detailed cost breakdown</h3>
            <p className="sh">
              Figures pre-tax unless stated; GST {p.gst_percent ?? 18}% added on invoice; government fees billed at cost.
            </p>
            <table className="pt"><tbody>
              <tr><td>Package</td><td>{quote.plan}</td></tr>
              <tr><td>List price / month</td><td>{inr(li.base_monthly)}</td></tr>
              {li.addons_monthly > 0 && (
                <tr>
                  <td>
                    Add-ons / month ({li.extra_users || 0} extra users
                    {li.extra_clients ? `, ${li.extra_clients} extra clients` : ""})
                  </td>
                  <td>{inr(li.addons_monthly)}</td>
                </tr>
              )}
              <tr><td>Subtotal / month</td><td>{inr(p.monthly_subtotal)}</td></tr>
              {d.discount_amount_monthly > 0 && (
                <tr>
                  <td>Discount ({discountLabel})</td>
                  <td style={{ color: "var(--emr)" }}>− {inr(d.discount_amount_monthly)}</td>
                </tr>
              )}
              <tr className="big"><td>Net price / month</td><td>{inr(p.monthly_net)}</td></tr>
              <tr>
                <td>Per {termWord} (× {months} month{months > 1 ? "s" : ""})</td>
                <td>{inr(p.per_term)}</td>
              </tr>
              <tr><td>GST ({p.gst_percent ?? 18}%)</td><td>{inr(p.gst)}</td></tr>
              <tr className="big"><td>{termLabel} invoice (incl. GST)</td><td>{inr(p.term_invoice)}</td></tr>
              {p.onboarding > 0 && (
                <>
                  <tr><td>One-time onboarding (list)</td><td>{inr(p.onboarding)}</td></tr>
                  {onboardingSaving > 0 && (
                    <tr>
                      <td>Onboarding discount ({d.onboarding_percent}%)</td>
                      <td style={{ color: "var(--emr)" }}>− {inr(onboardingSaving)}</td>
                    </tr>
                  )}
                  {p.onboarding_gst > 0 && (
                    <tr><td>GST on onboarding ({p.gst_percent ?? 18}%)</td><td>{inr(p.onboarding_gst)}</td></tr>
                  )}
                  <tr><td>First invoice ({firstLabel} + onboarding)</td><td>{inr(p.first_invoice)}</td></tr>
                </>
              )}
              <tr><td>Estimated annual value</td><td>{inr(p.annual_value)}</td></tr>
            </tbody></table>
          </div>
        )}

        {draw(breakdownBefore, sections.length)}

        <div className="contactband">
          <span className="mono">{(company.brand_name || "E").charAt(0)}</span>
          <h3>Let's protect {client}'s brand</h3>
          <p className="cbsub">Reply to this proposal to activate your 14-day free trial, or reach us on any channel below.</p>
          <div className="cbgrid">
            {has(company.website) && (
              <div className="cbitem"><div className="l">Website</div><div className="v">{company.website}</div></div>
            )}
            {has(company.email) && (
              <div className="cbitem"><div className="l">Email</div><div className="v">{company.email}</div></div>
            )}
            {has(company.whatsapp) && (
              <div className="cbitem"><div className="l">WhatsApp / Call</div><div className="v">{company.whatsapp}</div></div>
            )}
            {has(company.office_short) && (
              <div className="cbitem"><div className="l">Registered office</div><div className="v">{company.office_short}</div></div>
            )}
          </div>
          {/* The label stays the site the reader recognises; the link goes where
              they can actually book. */}
          <a className="cbcta" href={DEMO_URL} target="_blank" rel="noopener noreferrer">
            Book a live demo &nbsp;→&nbsp; {company.website}
          </a>
        </div>

        <div className="foot">
          {/* Assembled from the fields that exist: rendering them straight put a
              literal "null" on a client-facing page whenever the company profile
              left one blank. */}
          <b>{company.brand_name}</b>
          {joinParts([
            company.legal_name,
            labelled("CIN", company.cin),
            labelled("GSTIN", company.gstin),
            company.email,
            has(company.whatsapp) ? `WhatsApp ${company.whatsapp}` : "",
            company.website,
          ]) && ` — ${joinParts([
            company.legal_name,
            labelled("CIN", company.cin),
            labelled("GSTIN", company.gstin),
            company.email,
            has(company.whatsapp) ? `WhatsApp ${company.whatsapp}` : "",
            company.website,
          ])}`}
          <br />
          <span style={{ color: "#6E76A4" }}>
            {template?.footer_note && <>{fill(template.footer_note)} </>}
            {/* The reader needs to know how long these numbers hold. */}
            {validity ? `Valid for ${validity} days from ${fmtDate(today)}.` : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProposalDocument;
